import { Fragment, useState } from 'react';
import Panel from './Panel';
import SummaryCard from './SummaryCard';
import { exportCostaRicaCsv, exportCostaRicaExcel } from './exportCostaRica';

function fmtNumber(num) {
  return new Intl.NumberFormat('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
}

function LineItemsRow({ f }) {
  return (
    <tr>
      <td colSpan={8} className="px-6 py-4 bg-indigo-50/40 border-b border-indigo-100">
        <p className="text-xs text-indigo-400 font-mono mb-3 break-all">{f.clave}</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-gray-500 border-b border-indigo-100">
                <th className="pb-1.5 pr-4 font-medium">N°</th>
                <th className="pb-1.5 pr-4 font-medium">Cantidad</th>
                <th className="pb-1.5 pr-4 font-medium">Unidad</th>
                <th className="pb-1.5 pr-4 font-medium min-w-[200px]">Detalle</th>
                <th className="pb-1.5 pr-4 font-medium text-right">P. Unitario</th>
                <th className="pb-1.5 font-medium text-right">Total Línea</th>
              </tr>
            </thead>
            <tbody>
              {f.items.map((item, j) => (
                <tr key={j} className="border-t border-indigo-100/60">
                  <td className="py-1.5 pr-4 text-gray-400">{item.numLinea}</td>
                  <td className="py-1.5 pr-4 text-gray-700">{item.cantidad}</td>
                  <td className="py-1.5 pr-4 text-gray-500">{item.unidad}</td>
                  <td className="py-1.5 pr-4 text-gray-800">{item.detalle}</td>
                  <td className="py-1.5 pr-4 text-right tabular-nums text-gray-600">{fmtNumber(item.precioUnitario)}</td>
                  <td className="py-1.5 text-right tabular-nums font-semibold text-gray-800">{fmtNumber(item.montoTotalLinea)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-indigo-200">
                <td colSpan={5} className="pt-2 text-right text-gray-500 pr-4 font-medium">Total comprobante</td>
                <td className="pt-2 text-right tabular-nums font-bold text-indigo-700">{fmtNumber(f.totalComprobante)} {f.moneda}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </td>
    </tr>
  );
}

export default function CostaRicaPanel({ invoices, errors, onDismissErrors }) {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (clave) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(clave) ? next.delete(clave) : next.add(clave);
      return next;
    });
  };

  const totalGravado = invoices.reduce((s, f) => s + f.totalGravado, 0);
  const totalIVA = invoices.reduce((s, f) => s + f.totalImpuesto, 0);
  const totalComprobante = invoices.reduce((s, f) => s + f.totalComprobante, 0);

  return (
    <div id="panel-costa-rica" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-gray-900 leading-none">Costa Rica</h2>
          <p className="text-xs text-gray-400 mt-1">Comprobantes electrónicos · Hacienda v4.4</p>
        </div>
        {invoices.length > 0 && (
          <div className="flex gap-2">
            <button onClick={() => exportCostaRicaCsv(invoices)} className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg px-3 py-1.5 font-medium transition-colors">
              Exportar CSV
            </button>
            <button onClick={() => exportCostaRicaExcel(invoices)} className="text-xs bg-brand-600 hover:bg-brand-700 text-white rounded-lg px-3 py-1.5 font-medium transition-colors">
              Exportar Excel
            </button>
          </div>
        )}
      </div>

      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-red-700 font-semibold text-sm">
              {errors.length} archivo{errors.length !== 1 ? 's' : ''} de Costa Rica con error
            </h3>
            <button onClick={onDismissErrors} className="text-red-400 hover:text-red-600 text-xs">Descartar</button>
          </div>
          <ul className="space-y-1">
            {errors.map((e, i) => (
              <li key={i} className="text-red-600 text-xs">
                <span className="font-mono font-medium">{e.fileName}</span>
                {' — '}{e.error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {invoices.length === 0 && errors.length === 0 && (
        <p className="text-center text-gray-400 text-sm py-8">
          Importa comprobantes electrónicos de Costa Rica (factura, tiquete, notas de crédito/débito...) para comenzar
        </p>
      )}

      {invoices.length > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard label="Facturas cargadas" value={invoices.length} />
            <SummaryCard label="Total Gravado" value={`₡ ${fmtNumber(totalGravado)}`} />
            <SummaryCard label="Total IVA" value={`₡ ${fmtNumber(totalIVA)}`} />
            <SummaryCard label="Total Comprobante" value={`₡ ${fmtNumber(totalComprobante)}`} isHighlight />
          </div>

          <Panel title={`${invoices.length} documento${invoices.length !== 1 ? 's' : ''} · Clic en una fila para ver el detalle`}>
            <div className="overflow-x-auto -mx-4 -mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="w-8 px-3 py-3" />
                    <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Fecha</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Emisor</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600 hidden md:table-cell">Receptor</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600 hidden lg:table-cell">Tipo</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600 whitespace-nowrap">Gravado</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600 hidden lg:table-cell whitespace-nowrap">IVA</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600 whitespace-nowrap">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((f, i) => {
                    const isExpanded = expandedRows.has(f.clave);
                    return (
                      <Fragment key={f.clave || i}>
                        <tr
                          className={`border-b border-gray-100 cursor-pointer hover:bg-brand-500/5 transition-colors ${
                            i % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'
                          } ${isExpanded ? 'bg-indigo-50/30' : ''}`}
                          onClick={() => toggleRow(f.clave)}
                        >
                          <td className="px-3 py-3 text-gray-400 text-xs text-center select-none">
                            {isExpanded ? (
                              <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} className="inline text-indigo-400">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                              </svg>
                            ) : (
                              <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} className="inline text-gray-300">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                              </svg>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-700 whitespace-nowrap font-mono text-xs">{f.fecha}</td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-800 leading-snug">{f.emisorNombre}</div>
                            {f.emisorId && <div className="text-gray-400 text-xs font-mono">{f.emisorId}</div>}
                          </td>
                          <td className="px-4 py-3 text-gray-600 hidden md:table-cell text-xs leading-snug">{f.receptorNombre}</td>
                          <td className="px-4 py-3 hidden lg:table-cell">
                            <span className="bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded">{f.documentTypeLabel}</span>
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-gray-700">{fmtNumber(f.totalGravado)}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-gray-500 hidden lg:table-cell">{fmtNumber(f.totalImpuesto)}</td>
                          <td className="px-4 py-3 text-right tabular-nums font-bold text-gray-900">{fmtNumber(f.totalComprobante)}</td>
                        </tr>
                        {isExpanded && <LineItemsRow f={f} />}
                      </Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-200 bg-gray-50">
                    <td colSpan={5} className="px-4 py-3 text-xs text-gray-500 font-medium">Totales</td>
                    <td className="px-4 py-3 text-right tabular-nums font-bold text-gray-800">{fmtNumber(totalGravado)}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-bold text-gray-600 hidden lg:table-cell">{fmtNumber(totalIVA)}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-bold text-brand-700">{fmtNumber(totalComprobante)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}
